import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/gameConfig';

interface ResultData {
  score: number;
  foundTargets: number;
}

export class ResultScene extends Phaser.Scene {
  private score: number = 0;
  private foundTargets: number = 0;

  constructor() {
    super({ key: 'ResultScene' });
  }

  init(data: ResultData): void {
    this.score = data.score || 0;
    this.foundTargets = data.foundTargets || 0;
  }

  create(): void {
    const { WIDTH, HEIGHT, FONTS } = GAME_CONFIG;
    const centerX = WIDTH / 2;

    // 背景
    this.createBackground();

    // タイムオーバーテキスト（バウハウス：赤）
    const timeUpText = this.add.text(centerX, HEIGHT * 0.15, 'タイムオーバー', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.TITLE_SIZE}px`,
      color: '#e74c3c',
      fontStyle: 'bold',
    });
    timeUpText.setOrigin(0.5);

    // 結果パネル
    this.createResultPanel(centerX, HEIGHT * 0.45);

    // タイトルへ戻るボタン
    this.createTitleButton(centerX, HEIGHT * 0.75);

    // 結果アニメーション
    this.playResultAnimation();
  }

  private createBackground(): void {
    const { WIDTH, HEIGHT, COLORS } = GAME_CONFIG;

    // 背景（バウハウス：白）
    const graphics = this.add.graphics();
    graphics.fillStyle(COLORS.WHITE, 1);
    graphics.fillRect(0, 0, WIDTH, HEIGHT);

    // バウハウス的な幾何学図形
    const shapes = [
      { x: 100, y: 200, color: COLORS.PRIMARY, size: 40 },
      { x: WIDTH - 100, y: HEIGHT - 300, color: COLORS.YELLOW, size: 60 },
      { x: 200, y: HEIGHT - 150, color: COLORS.BLUE, size: 50 },
    ];
    shapes.forEach((s, i) => {
      if (i % 2 === 0) {
        this.add.rectangle(s.x, s.y, s.size, s.size, s.color, 0.2);
      } else {
        this.add.circle(s.x, s.y, s.size / 2, s.color, 0.2);
      }
    });
  }

  private createResultPanel(x: number, y: number): void {
    const { COLORS, FONTS } = GAME_CONFIG;

    // パネル背景（バウハウス：角張った矩形、黒枠）
    const panelBg = this.add.graphics();
    panelBg.fillStyle(COLORS.WHITE, 1);
    panelBg.fillRect(x - 280, y - 150, 560, 300);
    panelBg.lineStyle(4, COLORS.BLACK, 1);
    panelBg.strokeRect(x - 280, y - 150, 560, 300);

    // スコアラベル
    const scoreLabel = this.add.text(x, y - 80, 'スコア', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.MEDIUM_SIZE}px`,
      color: '#666666',
      fontStyle: 'bold',
    });
    scoreLabel.setOrigin(0.5);

    // スコア値
    const scoreValue = this.add.text(x, y, '0', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.TITLE_SIZE + 20}px`,
      color: '#000000',
      fontStyle: 'bold',
    });
    scoreValue.setOrigin(0.5);
    scoreValue.setName('scoreValue');

    // 見つけたおっさん数
    const itemsFound = this.add.text(
      x,
      y + 100,
      `見つけたおっさん: ${this.foundTargets}`,
      {
        fontFamily: FONTS.DEFAULT,
        fontSize: `${FONTS.SMALL_SIZE}px`,
        color: '#666666',
      }
    );
    itemsFound.setOrigin(0.5);
  }

  private playResultAnimation(): void {
    // スコアカウントアップアニメーション
    const scoreValue = this.children.getByName(
      'scoreValue'
    ) as Phaser.GameObjects.Text;

    if (scoreValue) {
      this.tweens.addCounter({
        from: 0,
        to: this.score,
        duration: 1500,
        ease: 'Power2',
        onUpdate: (tween) => {
          const value = Math.floor(tween.getValue() ?? 0);
          scoreValue.setText(value.toString());
        },
      });
    }
  }

  private createTitleButton(x: number, y: number): void {
    const { COLORS, FONTS } = GAME_CONFIG;

    // ボタン背景（バウハウス：白、黒枠）
    const buttonBg = this.add.graphics();
    buttonBg.fillStyle(COLORS.WHITE, 1);
    buttonBg.fillRect(-120, -25, 240, 50);
    buttonBg.lineStyle(2, COLORS.BLACK, 1);
    buttonBg.strokeRect(-120, -25, 240, 50);

    // ボタンテキスト
    const buttonText = this.add.text(0, 0, 'タイトルへ戻る', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.SMALL_SIZE}px`,
      color: '#000000',
      fontStyle: 'bold',
    });
    buttonText.setOrigin(0.5);

    // コンテナ
    const button = this.add.container(x, y, [buttonBg, buttonText]);

    // インタラクティブ設定
    const hitArea = new Phaser.Geom.Rectangle(-120, -25, 240, 50);
    button.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    // ホバー効果
    button.on('pointerover', () => {
      buttonText.setColor('#e74c3c');
    });

    button.on('pointerout', () => {
      buttonText.setColor('#000000');
    });

    // クリック時
    button.on('pointerdown', () => {
      this.sound.play('button');
      this.scene.start('TitleScene');
    });
  }
}
